import { useCallback, useEffect, useMemo, useState } from "react";
import { Alerta, Botao, Caminho, Campo, Cartao, SeletorDeEmpresa, Tabela } from "@plantus/ui";
import { fornecedores as api } from "@plantus/api-client";
import type { FornecedorCadastrado } from "@plantus/api-client";

import { usarEmpresas } from "../../../nucleo/empresa/ProvedorDeEmpresas";
import { caminhoDaRota } from "../../../nucleo/navegacao/caminho";
import { ROTAS } from "../../../nucleo/navegacao/rotas";
import { colunas } from "./PaginaFornecedoresColunas";

import "./fornecedores.css";

/**
 * A qualificação de fornecedores — de Assuntos Regulatórios.
 *
 * <h2>Por que esta tela existe</h2>
 *
 * Porque a de recebimento tinha um botão "+ Novo fornecedor". Quem estava
 * recebendo material cadastrava o fornecedor na hora — exatamente o que a
 * qualificação existe para impedir.
 *
 * <p>Aqui se cadastra e se decide; na Produção só se escolhe entre os
 * aprovados. Comprar de fornecedor não qualificado é desvio de BPF, e o
 * controle não pode depender de quem recebe saber quais estão qualificados.
 *
 * <h2>O consolidado, e por que ele é MAIS delicado aqui</h2>
 *
 * "Todas as empresas" junta as <b>três</b> — a distribuidora também compra, e
 * por isso ela aparece neste seletor e não no de matérias-primas.
 *
 * <p>E a qualificação é <b>por empresa</b> (invariante 5): um fornecedor
 * aprovado para a ProLab não está aprovado para a Plantus Óleos. No consolidado
 * o mesmo nome aparece uma vez por empresa em que foi cadastrado, e
 * possivelmente com situações diferentes — "Qualificado" numa linha e
 * "Suspenso" na de baixo, com o mesmo CNPJ de fornecedor.
 *
 * <p>Sem o selo de empresa em cada linha isso não seria informação, seria
 * armadilha: pareceria duplicidade ou contradição, e quem lê rápido decidiria
 * pela linha errada. Comprar de fornecedor não qualificado é desvio de BPF.
 *
 * <p>Cadastrar continua exigindo <b>uma</b> empresa escolhida: o cadastro nasce
 * para um CNPJ, e não existe fornecedor "de todas".
 *
 * <h2>O que ainda falta</h2>
 *
 * Qualificar é aprovar, e aprovar é <b>assinatura</b> (restrição 3). Hoje o
 * sistema registra autoria — quem, quando, e a trilha com valor anterior e
 * novo. A tela diz isso em vez de deixar parecer que a assinatura já existe.
 */


export function PaginaFornecedores() {
  const { lista, carregando, escolha, atual, consolidado, escolher } = usarEmpresas();

  /*
   * As empresas desta consulta: uma quando há escolha, as TRÊS no consolidado.
   *
   * Diferente da tela de matérias-primas, aqui não há filtro por papel — a
   * distribuidora compra, tem fornecedor e precisa qualificá-lo.
   */
  const empresasConsultadas = useMemo(
    () => (consolidado ? lista : atual !== null ? [atual] : []),
    [consolidado, lista, atual],
  );
  const chaveDasEmpresas = empresasConsultadas.map((e) => e.id).join(",");

  const [cadastrados, definirCadastrados] = useState<FornecedorCadastrado[]>([]);
  const [erro, definirErro] = useState<string | null>(null);
  const [cadastrando, definirCadastrando] = useState(false);
  const [nome, definirNome] = useState("");
  const [cnpj, definirCnpj] = useState("");

  const carregar = useCallback(() => {
    const ids = chaveDasEmpresas === "" ? [] : chaveDasEmpresas.split(",");
    if (ids.length === 0) {
      definirCadastrados([]);
      return;
    }
    api
      .listar(ids)
      .then(definirCadastrados)
      // UMA requisição, então falha é a lista inteira. Meia lista consolidada
      // apareceria completa, sem o cadastro de um CNPJ.
      .catch(() => definirErro("Não foi possível carregar os fornecedores."));
  }, [chaveDasEmpresas]);

  useEffect(() => carregar(), [carregar]);

  // Sair do consolidado com o cadastro aberto deixaria um formulário sem
  // empresa para gravar. Ele fecha junto.
  useEffect(() => {
    if (atual === null) definirCadastrando(false);
  }, [atual]);

  async function cadastrar() {
    if (atual === null) return;
    definirErro(null);
    try {
      await api.cadastrar(atual.id, nome, cnpj === "" ? null : cnpj);
      definirNome("");
      definirCnpj("");
      definirCadastrando(false);
      carregar();
    } catch {
      definirErro("Não foi possível cadastrar o fornecedor.");
    }
  }

  async function decidir(f: FornecedorCadastrado, acao: "qualificar" | "reprovar" | "suspender") {
    definirErro(null);
    try {
      if (acao === "qualificar") {
        await api.qualificar(f.id);
      } else {
        // O motivo é obrigatório no servidor. Pedi-lo aqui evita a viagem que
        // volta com erro — mas quem garante é o domínio, não este prompt.
        //
        // A EMPRESA vai na pergunta, e não é redundância com o selo da linha:
        // a qualificação é por empresa, e no consolidado o mesmo nome aparece
        // duas vezes. Este é o último ponto antes de uma decisão que RETIRA a
        // qualificação — perguntar "por que a Aroma Naturais?" sem dizer de
        // qual CNPJ é justamente onde o clique na linha vizinha passa.
        const deQuem = `${f.nome} (${f.empresa ?? "empresa não identificada"})`;
        const motivo = window.prompt(
          acao === "reprovar"
            ? `Por que ${deQuem} está sendo reprovada? O motivo fica na trilha.`
            : `Por que ${deQuem} está sendo suspensa? O motivo fica na trilha.`,
        );
        if (motivo === null || motivo.trim() === "") return;
        await (acao === "reprovar" ? api.reprovar(f.id, motivo) : api.suspender(f.id, motivo));
      }
      carregar();
    } catch {
      definirErro("Não foi possível registrar a decisão.");
    }
  }

  if (carregando) {
    return <p role="status">Carregando empresas…</p>;
  }

  return (
    /* Tela DENSA: seis colunas de tabela, e a pergunta é quantos fornecedores
       se veem sem rolar. Ver "A LEI DE LARGURA" em `app.css`. */
    <div className="lotes" data-largura="densa">
      <header className="lotes__topo">
        <div>
          {/* O chapéu escrito à mão dizia só "Assuntos Regulatórios", sem o
              módulo — e "Fornecedores" vai existir em mais de um setor, então
              era exatamente o degrau que faltava. O caminho traz os dois do
              mapa de navegação. */}
          <Caminho passos={caminhoDaRota(ROTAS.fornecedores)} />
          <h1 className="lotes__titulo">Fornecedores</h1>
          <p className="lotes__subtitulo">
            {consolidado
              ? `${lista.map((e) => e.apelido).join(" · ")} — cada linha diz de qual`
              : (atual?.nomeFantasia ?? "Escolha a empresa")}
          </p>
        </div>
        <SeletorDeEmpresa
          empresas={lista.map((e) => ({
            id: e.id,
            nome: e.razaoSocial,
            apelido: e.apelido,
            papel: e.papel,
          }))}
          selecionada={escolha ?? ""}
          aoSelecionar={escolher}
        />
      </header>

      {erro !== null && <Alerta tom="erro">{erro}</Alerta>}

      {atual === null && !consolidado ? (
        <Alerta tom="informacao">
          A qualificação é <strong>por empresa</strong>. Um fornecedor aprovado
          para a ProLab não está aprovado para a Plantus Óleos — outros
          produtos, outro processo, outra avaliação. Escolha uma, ou veja{" "}
          <strong>Todas as empresas</strong>: lá cada linha diz de quem é a
          qualificação.
        </Alerta>
      ) : (
        <>
          <Alerta tom="atencao">
            Qualificar é aprovar, e aprovação é assinatura. Hoje o sistema
            registra <strong>quem decidiu e quando</strong>, com a trilha
            completa — a assinatura eletrônica ainda não existe.
          </Alerta>

          {/* O MESMO botão alterna entre abrir e desistir, e a aparência tem
              de alternar junto: com o formulário aberto, "Cancelar" em verde
              sólido dava à tela DOIS botões primários de peso igual — um que
              grava o cadastro e outro que joga fora o que a pessoa digitou.
              CSS não resolve isto, porque folha de estilo não lê rótulo. */}
          <div className="lotes__acoes">
            {/* Cadastrar exige UMA empresa: o fornecedor nasce para um CNPJ, e
                a qualificação dele é daquela empresa. Desabilitado, e não
                escondido — o caminho continua visível, com a frase abaixo
                dizendo o que falta. */}
            <Botao
              type="button"
              aparencia={cadastrando ? "secundaria" : "primaria"}
              onClick={() => definirCadastrando((a) => !a)}
              disabled={atual === null}
            >
              {cadastrando ? "Cancelar" : "Cadastrar fornecedor"}
            </Botao>
            {consolidado && (
              <p className="lotes__vazio">
                Cadastrar exige uma empresa escolhida — a qualificação é dela,
                não do grupo.
              </p>
            )}
          </div>

          {cadastrando && atual !== null && (
            <Cartao titulo="Novo fornecedor">
              <div className="recebimento__aninhado">
                <Campo
                  rotulo="Razão social"
                  value={nome}
                  onChange={(e) => definirNome(e.target.value)}
                />
                <Campo
                  rotulo="CNPJ"
                  value={cnpj}
                  onChange={(e) => definirCnpj(e.target.value)}
                  dica="Opcional agora; obrigatório para qualificar de verdade."
                />
                <Alerta tom="informacao">
                  O cadastro entra como <strong>em qualificação</strong> e não
                  aparece no recebimento. Cadastrar não é qualificar.
                </Alerta>
                <Botao type="button" onClick={() => void cadastrar()}>
                  Cadastrar
                </Botao>
              </div>
            </Cartao>
          )}

          {cadastrados.length === 0 ? (
            <p className="lotes__vazio">
              {consolidado
                ? "Nenhum fornecedor cadastrado em nenhuma das empresas."
                : "Nenhum fornecedor cadastrado para esta empresa."}
            </p>
          ) : (
            /*
              A tabela mora dentro de um `Cartao`. Ela pousava direto no papel
              cinza-100 da página, e isso já tinha custado uma medição: a
              legenda dava 4.05:1 ali e reprovava, o que empurrou o piso de
              `--tinta-sutil` a subir na base do tema.

              O contraste não é mais o motivo — o piso subiu e a legenda passa
              nas quatro superfícies. O motivo é HIERARQUIA, e ele ficou
              impossível de ignorar quando a moldura escurecia ao lado: com o
              cartão em relevo em toda tela vizinha, uma tabela solta no papel
              lê-se como bloco inacabado. Fica no branco (5.39:1 na legenda,
              9.12:1 na tinta crítica de "Reprovar") e ganha a mesma borda,
              sombra e respiro de todo dado do sistema.

              ⚠️ AQUI DIZIA "como a de lotes", E DEIXOU DE SER VERDADE em
              2026-08-14: o `Cartao` saiu de volta da lista de lotes, porque
              lá a tabela não é um bloco DA tela — ela É a tela, e uma moldura
              em volta do assunto da página anuncia a mesma informação três
              vezes. Aqui a tabela é um bloco entre outros (há o cadastro e o
              dossiê ao lado), e por isso a moldura continua fazendo sentido.
              As duas telas divergiram de propósito; o que não podia ficar é
              uma delas citando a outra como se ainda fossem iguais.

              🔴 E O CARTÃO GANHOU UM INVÓLUCRO NOMEADO, que não é desenho:
              sem ele a tabela NÃO IA AO PAPEL. A raiz desta tela é `.lotes`,
              e a lista de permitidos de `@media print` (em `app.css`) nomeia
              os filhos diretos dela — `section.ui-cartao` não está em nome
              nenhum, e o que a folha trazia era título, empresa e um aviso,
              sem uma linha de fornecedor. Medido em A4 paisagem: 0 × 0 mm.

              Admitir `.ui-cartao` na lista levaria junto o cartão de "Novo
              fornecedor", que é ENTRADA — uma moldura vazia no papel. É o
              mesmo par DADO/ENTRADA que `.lotes__quarentena` resolve na tela
              de lotes, e a saída é a mesma: um invólucro com nome.
            */
            <div className="fornecedores__lista">
              <Cartao titulo="Fornecedores cadastrados">
                <Tabela
                  colunas={colunas(decidir, consolidado)}
                  linhas={cadastrados}
                  chave={(f) => f.id}
                  legenda={
                    <>
                      Só os <strong>qualificados</strong> aparecem na tela de
                      recebimento da Produção.
                      {consolidado && (
                        <>
                          {" "}
                          A qualificação é <strong>por empresa</strong>: o mesmo
                          fornecedor pode aparecer mais de uma vez, em situações
                          diferentes.
                        </>
                      )}
                    </>
                  }
                />
              </Cartao>
            </div>
          )}
        </>
      )}
    </div>
  );
}
